import { useState } from "react";
import { PauseCircle } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PermissionButton } from "@/components/ui/permission-button";
import { Textarea } from "@/components/ui/textarea";
import { useHoldItemMutation } from "@/services/(company)/payrollRunsApi";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";

/**
 * Hold one salary straight from the run's list (B8). Hold existed only as a
 * switch on the item's edit page, which testers could not find.
 */
export const HoldRowButton = ({
  runId,
  itemId,
}: {
  runId: number;
  itemId: number;
}) => {
  const { t } = useTranslation(["company", "common"]);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [holdItem, { isLoading }] = useHoldItemMutation();

  const submit = async () => {
    try {
      await holdItem({ runId, itemId, reason: reason.trim() }).unwrap();
      toast.success(t("company:payrolls.salary_put_on_hold"));
      setOpen(false);
      setReason("");
    } catch (err: unknown) {
      const msg = (err as { data?: { message?: unknown } })?.data?.message;
      toast.error(
        Array.isArray(msg)
          ? msg.join(", ")
          : String(msg ?? t("company:payrolls.failed_to_hold_salary")),
      );
    }
  };

  return (
    <>
      <PermissionButton
        section={PLATFORM_SECTION_ENUM.PAYROLL}
        action="edit"
        mode="hide"
        variant="outline-warning"
        size="icon-sm"
        aria-label={t("company:payrolls.hold_salary_title")}
        data-tooltip-id="hrm-tooltip"
        data-tooltip-content={t("company:payrolls.hold_salary_title")}
        onClick={() => setOpen(true)}
      >
        <PauseCircle size={14} />
      </PermissionButton>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("company:payrolls.hold_salary_title")}</DialogTitle>
            <DialogDescription>
              {t("company:payrolls.hold_row_desc")}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("company:payrolls.hold_reason_placeholder")}
            aria-label={t("company:payrolls.hold_reason_placeholder")}
            rows={3}
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {t("common:buttons.cancel")}
            </Button>
            <Button
              type="button"
              variant="warning"
              disabled={reason.trim().length < 3 || isLoading}
              onClick={submit}
            >
              {t("company:payrolls.hold_salary_title")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
